import { useCallback, useLayoutEffect, useRef, useState } from 'react'
import { useRecoilValue } from 'recoil'

import { itemDraggingAtom } from '@/atoms/itemControlAtoms'
import {
  captureNaturalOffsets,
  DOCK_COLLISION_PAD_PX,
  inflateRect,
  naturalRectFromOffsets,
  rectsIntersect,
  type NaturalBarOffsets,
} from '@/utils/editor/overlayDock'

export type DockSide = 'top' | 'bottom'

type UseEditDockSideArgs = {
  active: boolean
  x: number
  y: number
  width: number
  height: number
  zoom: number
}

type UseEditDockSideResult = {
  barRef: React.MutableRefObject<HTMLDivElement | null>
  dockSide: DockSide
}

export const useEditDockSide = ({
  active,
  x,
  y,
  width,
  height,
  zoom,
}: UseEditDockSideArgs): UseEditDockSideResult => {
  const barRef = useRef<HTMLDivElement | null>(null)
  const [dockSide, setDockSide] = useState<DockSide>('top')
  const dockSideRef = useRef<DockSide>('top')
  dockSideRef.current = dockSide
  const offsetsRef = useRef<NaturalBarOffsets | null>(null)

  const itemDragging = useRecoilValue(itemDraggingAtom)
  const frozenRef = useRef(false)
  frozenRef.current = itemDragging

  const measure = useCallback(() => {
    if (frozenRef.current) return
    const bar = barRef.current
    const wrapper = bar?.parentElement
    const pill = document.querySelector('[data-editor-text-toolbar]')
    if (!bar || !wrapper || !pill) return
    const wrapperRect = wrapper.getBoundingClientRect()
    let natural
    if (dockSideRef.current === 'top') {
      const barRect = bar.getBoundingClientRect()
      offsetsRef.current = captureNaturalOffsets(wrapperRect, barRect)
      natural = barRect
    } else if (offsetsRef.current) {
      natural = naturalRectFromOffsets(wrapperRect, offsetsRef.current)
    } else {
      setDockSide('top')
      return
    }
    const collide = rectsIntersect(
      inflateRect(pill.getBoundingClientRect(), DOCK_COLLISION_PAD_PX),
      natural,
    )
    const next: DockSide = collide ? 'bottom' : 'top'
    setDockSide((prev) => (prev === next ? prev : next))
  }, [])

  useLayoutEffect(() => {
    if (!active) {
      offsetsRef.current = null
      setDockSide((prev) => (prev === 'top' ? prev : 'top'))
      return undefined
    }
    measure()
    const canvas = document.querySelector('[data-editor-ui="editor-canvas"]')
    const pill = document.querySelector('[data-editor-text-toolbar]')
    const ro =
      typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null
    if (pill) ro?.observe(pill)
    window.addEventListener('resize', measure)
    canvas?.addEventListener('scroll', measure, { passive: true })
    return () => {
      ro?.disconnect()
      window.removeEventListener('resize', measure)
      canvas?.removeEventListener('scroll', measure)
    }
  }, [active, measure])

  useLayoutEffect(() => {
    if (active) measure()
  }, [active, measure, x, y, width, height, zoom, itemDragging])

  return { barRef, dockSide }
}
